import {
  Box,
  Drawer as MuiDrawer,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { PrivateRoute } from "app";
import clsx from "clsx";
import AnimalGroupDetail from "features/animalGroupDetail/AnimalGroupDetail";
import AnimalGroupInput from "features/animalGroupDetail/AnimalGroupInput";
import { Covers } from "features/bulkCoverInput";
import { CombinedInputsOutputsDetail } from "features/combinedInputsOutputs";
import { FarmView } from "features/farmOverview";
import { FarmInput } from "features/farmOverview/FarmInput";
import FieldsListScreen from "features/farmOverview/FieldsListScreen";
import { FieldInput, FieldView } from "features/fieldDetail";
import AddNewFile from "features/fileManagement/AddNewFile";
import FileDownload from "features/fileManagement/FileDownload";
import FileEntryDetail from "features/fileManagement/FileManagementDetail";
import InitialSelectScreen from "features/initialSelectScreen/InitialSelectScreen";
import AccessibleFarmsInsightsDrawer from "features/insights/AccessibleFarmsInsightsDrawer";
import FarmInsightsDrawer from "features/insights/FarmInsightsDrawer";
import ProjectInsightsDrawer from "features/insights/ProjectsInsightsDrawer";
import ProgrammeDetailsDrawer from "features/programmesActions/ProgrammesDetailsDrawer";
import ProgrammesDrawer from "features/programmesActions/ProgrammesDrawer";
import { UpdateBiomassAfterMoveInDialog } from "features/singleCoverInput";
import { MobileProfileScreen } from "features/userProfile/Profile";
import { useAppSelector } from "model";
import { MutableRefObject } from "react";
import { MapRef } from "react-map-gl";
import { Switch, useRouteMatch } from "react-router-dom";
import { drawerWidth } from "width";

const PREFIX = "Drawer";

const classes = {
  root: `${PREFIX}-root`,
  drawer: `${PREFIX}-drawer`,
  drawerOpen: `${PREFIX}-drawerOpen`,
  drawerClose: `${PREFIX}-drawerClose`,
  drawerContent: `${PREFIX}-drawerContent`,
};
interface StyledDrawerProps {
  theme: Theme;
  isHomeOrFarm: boolean;
}

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const StyledDrawer = styled("div")<StyledDrawerProps>(
  ({ theme, isHomeOrFarm }) => ({
    [`& .${classes.root}`]: {
      display: "flex",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },

    [`& .${classes.drawerOpen}`]: {
      [theme.breakpoints.up("sm")]: {
        width: drawerWidth,
      },
      [theme.breakpoints.down("sm")]: {
        width: (props: { isHomeOrFarm: boolean }) =>
          props.isHomeOrFarm ? "95%" : "100%",
      },
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      overflow: "visible",
    },

    [`& .${classes.drawerClose}`]: {
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: 0,
      overflow: "visible",
    },

    [`& .${classes.drawerContent}`]: {
      overflowX: "hidden",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    },
  })
);

interface DrawerProps {
  mapRef: MutableRefObject<MapRef | null>;
}

export default function Drawer({ mapRef }: DrawerProps) {
  const open = useAppSelector((state) => state.app.drawerOpen);
  const initalScreenSelectMatch = useRouteMatch("/initial-screen-select");
  const farmOverviewMatch = useRouteMatch("/farm/:farmId");
  const addFarmMatch = useRouteMatch("/add-farm");
  const fieldMatch = useRouteMatch("/field/:fieldId");
  const fieldsListMatch = useRouteMatch("/fields-list/:fieldType");
  const addFieldMatch = useRouteMatch("/add-field");
  const animalGroupMatch = useRouteMatch("/animal-group/:animalGroupId");
  const addAnimalGroupMatch = useRouteMatch("/add-animal-group");
  const editAnimalGroupMatch = useRouteMatch(
    "/edit-animal-group/:animalGroupId"
  );
  const editFieldMatch = useRouteMatch("/edit-field/:fieldId");
  const addCoversMatch = useRouteMatch("/add-covers");
  const fileManagementMatch = useRouteMatch("/file-management-overview");
  const inputsAndOutputsMatch = useRouteMatch("/inputs-and-outputs");
  const isHomeOrFarm =
    useRouteMatch({ path: ["/farm/:farmId", "/"], exact: true }) !== null;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const farmInsightsMatch = useRouteMatch("/farm-insights");
  const accessibleFarmInsightsMatch = useRouteMatch(
    "/accessible-farms-insights"
  );
  const projectInsightsMatch = useRouteMatch("/project-insights");
  const programmesMatch = useRouteMatch("/programmes");
  const programmeDetailsMatch = useRouteMatch(
    "/programme-details/:programmeId"
  );
  const backgroundColor =
    initalScreenSelectMatch ||
    (farmInsightsMatch && !isMobile) ||
    (projectInsightsMatch && !isMobile) ||
    (accessibleFarmInsightsMatch && !isMobile) ||
    (programmesMatch && !isMobile) ||
    (programmeDetailsMatch && !isMobile) ||
    (fileManagementMatch && !isMobile) ||
    (farmOverviewMatch && !isMobile) ||
    (addFarmMatch && !isMobile) ||
    (fieldMatch && !isMobile) ||
    (fieldsListMatch && !isMobile) ||
    (addFieldMatch && !isMobile) ||
    (editFieldMatch && !isMobile) ||
    (animalGroupMatch && !isMobile) ||
    (addAnimalGroupMatch && !isMobile) ||
    (editAnimalGroupMatch && !isMobile) ||
    (addCoversMatch && !isMobile) ||
    (inputsAndOutputsMatch && !isMobile)
      ? "#1C4321"
      : "white";
  return (
    <StyledDrawer isHomeOrFarm={isHomeOrFarm} theme={theme}>
      <MuiDrawer
        PaperProps={{
          sx: {
            backgroundColor:
              farmOverviewMatch ||
              addFarmMatch ||
              fieldMatch ||
              fieldsListMatch ||
              addFieldMatch ||
              animalGroupMatch ||
              addAnimalGroupMatch ||
              editAnimalGroupMatch ||
              editFieldMatch ||
              addCoversMatch
                ? "transparent"
                : backgroundColor,
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
            pointerEvents:
              farmOverviewMatch ||
              addFarmMatch ||
              addFieldMatch ||
              editFieldMatch ||
              addCoversMatch
                ? { xs: "none", sm: "auto" }
                : "auto",
            border: "none",
            borderRight: "1px solid #e4e4e7",
          },
        }}
        variant="permanent"
        className={clsx(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: clsx({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "100%",
            },
            backgroundColor:
              initalScreenSelectMatch ||
              (programmesMatch && !isMobile) ||
              (programmeDetailsMatch && !isMobile) ||
              (farmInsightsMatch && !isMobile) ||
              (projectInsightsMatch && !isMobile) ||
              (accessibleFarmInsightsMatch && !isMobile) ||
              (fileManagementMatch && !isMobile) ||
              (farmOverviewMatch && !isMobile) ||
              (addFarmMatch && !isMobile) ||
              (fieldMatch && !isMobile) ||
              (fieldsListMatch && !isMobile) ||
              (addFieldMatch && !isMobile) ||
              (editFieldMatch && !isMobile) ||
              (animalGroupMatch && !isMobile) ||
              (addAnimalGroupMatch && !isMobile) ||
              (editAnimalGroupMatch && !isMobile) ||
              (addCoversMatch && !isMobile) ||
              (inputsAndOutputsMatch && !isMobile)
                ? "#1C4321"
                : "white",
            pointerEvents: "auto",
          }}
        >
          <UpdateBiomassAfterMoveInDialog />
        </Box>
        <Box
          sx={{
            width: {
              xs: "100%",
              sm: "100%",
            },
            backgroundColor:
              farmOverviewMatch ||
              addFarmMatch ||
              addFieldMatch ||
              editFieldMatch ||
              addCoversMatch
                ? { xs: "transparent", sm: isMobile ? "white" : "#1C4321" }
                : animalGroupMatch ||
                  editAnimalGroupMatch ||
                  addAnimalGroupMatch ||
                  fieldMatch ||
                  fieldsListMatch
                ? { xs: "white", sm: isMobile ? "white" : "#1C4321" }
                : initalScreenSelectMatch ||
                  (programmesMatch && !isMobile) ||
                  (programmeDetailsMatch && !isMobile) ||
                  (farmInsightsMatch && !isMobile) ||
                  (projectInsightsMatch && !isMobile) ||
                  (accessibleFarmInsightsMatch && !isMobile) ||
                  (fileManagementMatch && !isMobile) ||
                  (farmOverviewMatch && !isMobile) ||
                  (addFarmMatch && !isMobile) ||
                  (fieldMatch && !isMobile) ||
                  (fieldsListMatch && !isMobile) ||
                  (addFieldMatch && !isMobile) ||
                  (editFieldMatch && !isMobile) ||
                  (animalGroupMatch && !isMobile) ||
                  (addAnimalGroupMatch && !isMobile) ||
                  (editAnimalGroupMatch && !isMobile) ||
                  (addCoversMatch && !isMobile) ||
                  (inputsAndOutputsMatch && !isMobile)
                ? "#1C4321"
                : "white",
            height: "100%",
            overflow: "auto",
            paddingBottom: {
              xs: "80px",
              sm: 0,
            },
          }}
        >
          <div className={classes.drawerContent}>
            <Switch>
              {isMobile && (
                <PrivateRoute
                  path="/farm/:farmId"
                  exact
                  children={<FarmView />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/farm/:farmId"
                  exact
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/add-farm"
                  children={<FarmInput mapRef={mapRef} />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/add-farm"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/fields-list/:fieldType"
                  children={<FieldsListScreen />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/fields-list/:fieldType"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/field/:fieldId"
                  exact
                  children={<FieldView />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/field/:fieldId"
                  exact
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/edit-field/:fieldId"
                  children={<FieldInput />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/edit-field/:fieldId"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute path="/add-field" children={<FieldInput />} />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/add-field"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/animal-group/:animalGroupId"
                  children={<AnimalGroupDetail />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/animal-group/:animalGroupId"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/edit-animal-group/:animalGroupId"
                  exact
                  children={<AnimalGroupInput />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/edit-animal-group/:animalGroupId"
                  exact
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/add-animal-group"
                  exact
                  children={<AnimalGroupInput />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/add-animal-group"
                  exact
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute path="/add-covers" children={<Covers />} />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/add-covers"
                  children={<InitialSelectScreen />}
                />
              )}
              <PrivateRoute
                path="/initial-screen-select"
                children={<InitialSelectScreen />}
              />
              {isMobile && (
                <PrivateRoute
                  path="/inputs-and-outputs"
                  children={<CombinedInputsOutputsDetail />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/inputs-and-outputs"
                  children={<InitialSelectScreen />}
                />
              )}
              <PrivateRoute
                path="/profile"
                children={<MobileProfileScreen />}
              />
              {isMobile && (
                <PrivateRoute
                  path="/file-management-overview"
                  exact
                  children={<FileEntryDetail />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/file-management-overview"
                  exact
                  children={<InitialSelectScreen />}
                />
              )}
              <PrivateRoute
                path="/file-download/:fileEntryId/:goBackParam"
                children={<FileDownload />}
              />
              <PrivateRoute path="/add-new-file" children={<AddNewFile />} />
              {isMobile && (
                <PrivateRoute
                  path="/farm-insights"
                  children={<FarmInsightsDrawer />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/farm-insights"
                  children={<InitialSelectScreen />}
                />
              )}

              {isMobile && (
                <PrivateRoute
                  path="/accessible-farms-insights"
                  children={<AccessibleFarmsInsightsDrawer />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/accessible-farms-insights"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/programmes"
                  children={<ProgrammesDrawer />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/programmes"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/programme-details/:programmeId"
                  children={<ProgrammeDetailsDrawer />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/programme-details/:programmeId"
                  children={<InitialSelectScreen />}
                />
              )}
              {isMobile && (
                <PrivateRoute
                  path="/project-insights"
                  children={<ProjectInsightsDrawer />}
                />
              )}
              {!isMobile && (
                <PrivateRoute
                  path="/project-insights"
                  children={<InitialSelectScreen />}
                />
              )}
            </Switch>
          </div>
        </Box>
      </MuiDrawer>
    </StyledDrawer>
  );
}
